import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
    FiMail, 
    FiLock, 
    FiEye, 
    FiEyeOff, 
    FiArrowRight, 
    FiBriefcase, 
    FiAlertCircle, 
    FiCheckCircle, 
    FiHelpCircle,
    FiExternalLink
} from 'react-icons/fi';
import { setCookie } from '../lib/cookieUtils';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/ithyaraa-logo.png';
import './Login.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://backend.ithyaraa.com/api';

function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [showHelpModal, setShowHelpModal] = useState(false);

    const navigate = useNavigate();
    const { isAuthenticated, checkAuthStatus } = useAuth();

    // Redirect if already authenticated as brand partner
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        setError('');

        if (!email.trim() || !password) {
            setError('Please enter both work email and password.');
            toast.warn('Please fill in all credentials');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(
                `${API_BASE_URL}/brand/login`,
                { email: email.trim(), password }
            );

            const data = response.data || {};

            if (data.success) {
                const days = rememberMe ? 30 : 3;
                if (data.accessToken) setCookie('brand_access_token', data.accessToken, days);
                if (data.refreshToken) setCookie('brand_refresh_token', data.refreshToken, 30);

                toast.success('Welcome to your Brand Portal!');
                if (checkAuthStatus) {
                    checkAuthStatus();
                }
                navigate('/');
            } else {
                setError(data.message || 'Brand login failed');
                toast.error(data.message || 'Brand login failed');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Login failed';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="ith-login-wrapper">
            <div className="ith-login-card">
                {/* Brand Header */}
                <div className="ith-brand-header">
                    <div className="ith-logo-wrap">
                        <img src={logo} alt="Ithyaraa" className="ith-logo-img" />
                    </div>
                    <div className="ith-badge">
                        <FiBriefcase size={11} />
                        BRAND PORTAL
                    </div>
                    <h1 className="ith-title">Brand Partner Portal</h1>
                    <p className="ith-subtitle">
                        Sign in to manage your brand inventory, orders & settlements
                    </p>
                </div>

                {/* Error Banner */}
                {error && (
                    <div className="ith-error-banner" role="alert">
                        <FiAlertCircle size={17} />
                        <div>{error}</div>
                    </div>
                )}

                {/* Login Form */}
                <form onSubmit={handleSubmit} noValidate>
                    <div className="ith-form-group">
                        {/* Email */}
                        <div className="ith-field">
                            <label className="ith-label" htmlFor="brand-email">
                                <span>Brand Work Email</span>
                            </label>
                            <div className="ith-input-wrap">
                                <span className="ith-icon-left">
                                    <FiMail />
                                </span>
                                <input
                                    id="brand-email"
                                    type="email"
                                    className="ith-input"
                                    placeholder="partner@brand.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="email"
                                    autoFocus
                                    required
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div className="ith-field">
                            <div className="ith-label">
                                <label htmlFor="brand-password">Password</label>
                                <button
                                    type="button"
                                    className="ith-link"
                                    onClick={() => setShowHelpModal(true)}
                                >
                                    Forgot password?
                                </button>
                            </div>
                            <div className="ith-input-wrap">
                                <span className="ith-icon-left">
                                    <FiLock />
                                </span>
                                <input
                                    id="brand-password"
                                    type={showPassword ? 'text' : 'password'}
                                    className="ith-input has-right-icon"
                                    placeholder="••••••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="ith-icon-right"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                </button>
                            </div>
                        </div>

                        {/* Options */}
                        <div className="ith-form-row">
                            <label className="ith-checkbox-label">
                                <input
                                    type="checkbox"
                                    className="ith-checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                />
                                <span>Remember this device</span>
                            </label>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="ith-submit-btn"
                    >
                        {loading ? (
                            <>
                                <span className="ith-spinner" />
                                <span>Authenticating...</span>
                            </>
                        ) : (
                            <>
                                <span>Sign In to Brand Portal</span>
                                <FiArrowRight size={16} />
                            </>
                        )}
                    </button>
                </form>

                {/* Brand Onboarding Link */}
                <div className="ith-onboarding-link">
                    Looking to sell your products on Ithyaraa?{' '}
                    <a
                        href="https://ithyaraa.com/brand-onboarding"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ith-link"
                        style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}
                    >
                        Apply for Partnership <FiExternalLink size={12} />
                    </a>
                </div>

                {/* Footer */}
                <div className="ith-footer">
                    <div className="ith-security">
                        <FiCheckCircle size={13} />
                        <span>256-Bit SSL Encrypted Enterprise Gateway</span>
                    </div>
                    <div className="ith-copyright">
                        © {new Date().getFullYear()} Ithyaraa Global Marketplace. All rights reserved.
                    </div>
                </div>
            </div>

            {/* Forgot Password / Help Modal */}
            {showHelpModal && (
                <div 
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: 20
                    }}
                    onClick={() => setShowHelpModal(false)}
                >
                    <div 
                        style={{
                            width: '100%',
                            maxWidth: 420,
                            background: '#0F172A',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: 16,
                            padding: 28,
                            color: '#F8FAFC',
                            boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(59, 130, 246, 0.15)', color: '#60A5FA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FiHelpCircle size={20} />
                            </div>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Account Recovery</h3>
                        </div>
                        <p style={{ fontSize: 13.5, color: '#94A3B8', lineHeight: 1.5, margin: '0 0 16px 0' }}>
                            For brand security and merchant data protection, credentials must be verified through brand relations.
                        </p>
                        <div style={{ background: 'rgba(255, 255, 255, 0.04)', borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13 }}>
                            <div style={{ color: '#E2E8F0', fontWeight: 600, marginBottom: 4 }}>Brand Relations & Support:</div>
                            <div style={{ color: '#60A5FA' }}>brands@ithyaraa.com</div>
                            <div style={{ color: '#64748B', fontSize: 12, marginTop: 4 }}>Merchant Support SLA: Under 2 hours</div>
                        </div>
                        <button
                            type="button"
                            className="ith-submit-btn"
                            style={{ margin: 0, height: 40 }}
                            onClick={() => setShowHelpModal(false)}
                        >
                            Understood
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Login;
